import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { customMultiSelectStyles } from "commons/styles/CustomStyles";
import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";
import { useAgentNextCallGroupFlow } from "hooks/UseAgentNextCallGroupFlow";
import { sendAuthenticatedAsyncRequestWithFinallyCallback } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import {
  buildNextCallGroupFiltered,
  clientOptionsForCustomers,
  normalizeSelectValue,
  toSelectOptions,
} from "./callGroupFilterHelpers";
import {
  loadStickyFilterSelections,
  saveStickyFilterSelections,
} from "./callGroupFilterSessionStorage";
import AssignedActivitiesTable from "./AssignedActivitiesTable";
import FilteredCallGroupsCompleteModal from "./FilteredCallGroupsCompleteModal";

const FILTER_PLACEHOLDERS = {
  customerNameAbbreviation: "All customers",
  clientNameAbbreviation: "All clients",
  type: "All types",
  status: "All statuses",
  entityType: "All entities",
};

const FILTER_LABELS = {
  customerNameAbbreviation: "Customer",
  clientNameAbbreviation: "Client",
  type: "Type",
  status: "Status",
  entityType: "Entity",
};

/** Layout matches mockup: 3 on first row, 2 on second. */
const FILTER_ROWS = [
  ["customerNameAbbreviation", "clientNameAbbreviation", "type"],
  ["status", "entityType"],
];

const emptySelections = () =>
  AgentWorkspaceConstants.AGENT_NEXT_CALL_GROUP_FILTER_IDS.reduce((acc, id) => {
    acc[id] = [];
    return acc;
  }, {});

/**
 * Drops sticky options that are no longer present in the current filter metadata.
 */
function pruneSelectionsAgainstMeta(selections, filtersMeta) {
  if (!filtersMeta || !selections) {
    return emptySelections();
  }
  const next = emptySelections();
  next.customerNameAbbreviation = normalizeSelectValue(
    selections.customerNameAbbreviation
  ).filter((opt) =>
    (filtersMeta.customerNameAbbreviation || []).some((row) => row.id === opt.value)
  );
  const allowedClients = new Set(
    clientOptionsForCustomers(filtersMeta, next.customerNameAbbreviation).map(
      (o) => o.value
    )
  );
  next.clientNameAbbreviation = normalizeSelectValue(
    selections.clientNameAbbreviation
  ).filter((opt) => allowedClients.has(opt.value));
  for (const id of ["type", "status", "entityType"]) {
    const metaRows = filtersMeta[id] || [];
    next[id] = normalizeSelectValue(selections[id]).filter((opt) =>
      metaRows.some((row) => row.id === opt.value)
    );
  }
  return next;
}

const CallGroupFilterScreen = () => {
  const navigate = useNavigate();
  const [flowEnabled, flowLoading] = useAgentNextCallGroupFlow();
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selections, setSelections] = useState(emptySelections);
  const [submitting, setSubmitting] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  useEffect(() => {
    if (flowLoading) {
      return;
    }
    if (!flowEnabled) {
      navigate("/agent-workspace/list", { replace: true });
    }
  }, [flowEnabled, flowLoading, navigate]);

  useEffect(() => {
    if (!flowEnabled) {
      return;
    }
    setFiltersLoading(true);
    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/agent-workspace/call-group/list-filters?showBioSimilar=false",
      "GET",
      null,
      (response) => {
        const meta = response.data.filters || {};
        setFiltersMeta(meta);
        const sticky = loadStickyFilterSelections();
        setSelections(
          sticky ? pruneSelectionsAgainstMeta(sticky, meta) : emptySelections()
        );
      },
      (error) => {
        showNotification({
          title: "Error fetching filters",
          message: getErrorMessage(error) || "Unable to load call group filters.",
          position: "tr",
          type: "error",
        });
      },
      () => setFiltersLoading(false)
    );
  }, [flowEnabled]);

  const optionMaps = useMemo(() => {
    if (!filtersMeta) {
      return {};
    }
    return {
      customerNameAbbreviation: toSelectOptions(filtersMeta.customerNameAbbreviation),
      clientNameAbbreviation: clientOptionsForCustomers(
        filtersMeta,
        selections.customerNameAbbreviation
      ),
      type: toSelectOptions(filtersMeta.type),
      // Same option set as Call Group List (includes ALL for status).
      status: toSelectOptions(filtersMeta.status),
      entityType: toSelectOptions(filtersMeta.entityType),
    };
  }, [filtersMeta, selections.customerNameAbbreviation]);

  const handleSelectionChange = (filterId, selected) => {
    setShowCompleteModal(false);
    setSelections((prev) => {
      const next = { ...prev, [filterId]: selected };
      if (filterId === "customerNameAbbreviation") {
        const allowed = new Set(
          clientOptionsForCustomers(filtersMeta, next.customerNameAbbreviation).map(
            (o) => o.value
          )
        );
        next.clientNameAbbreviation = normalizeSelectValue(prev.clientNameAbbreviation).filter(
          (c) => allowed.has(c.value)
        );
      }
      saveStickyFilterSelections(next);
      return next;
    });
  };

  const handleGo = (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setShowCompleteModal(false);
    setSubmitting(true);
    saveStickyFilterSelections(selections);
    const filtered = buildNextCallGroupFiltered(selections);
    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/agent-workspace/call-group/next",
      "POST",
      { filtered },
      (response) => {
        if (response.status === 204 || !response.data?.callGroupId) {
          setShowCompleteModal(true);
          return;
        }
        const statusFilter = filtered.find((f) => f.id === "status")?.value ?? null;
        navigate(`/agent-workspace/call-group-details/${response.data.callGroupId}`, {
          state: { status: statusFilter, fromAgentFilterScreen: true },
        });
      },
      (error) => {
        if (error?.status === 404) {
          showNotification({
            title: "Feature unavailable",
            message: "The next call group flow is not enabled for this environment.",
            position: "tr",
            type: "warning",
          });
          navigate("/agent-workspace/list", { replace: true });
          return;
        }
        showNotification({
          title: "Unable to load next call group",
          message: getErrorMessage(error) || "Please try again.",
          position: "tr",
          type: "error",
        });
      },
      () => setSubmitting(false)
    );
  };

  const FILTER_TEST_IDS = {
    customerNameAbbreviation: "filter-customer",
    clientNameAbbreviation: "filter-client",
    type: "filter-type",
    status: "filter-status",
    entityType: "filter-entity-type",
  };

  const renderFilterSelect = (filterId) => (
    <Form.Group className="mb-3" controlId={`filter-${filterId}`} key={filterId}>
      <Form.Label className="fw-semibold">{FILTER_LABELS[filterId]}</Form.Label>
      <div data-testid={FILTER_TEST_IDS[filterId]}>
        <Select
          isMulti
          isClearable
          options={optionMaps[filterId] || []}
          value={selections[filterId]}
          onChange={(selected) => handleSelectionChange(filterId, selected || [])}
          styles={customMultiSelectStyles}
          placeholder={FILTER_PLACEHOLDERS[filterId]}
          classNamePrefix="agent-filter"
          inputId={`agent-filter-${filterId}`}
        />
      </div>
    </Form.Group>
  );

  if (flowLoading || !flowEnabled) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4" data-testid="agent-filter-screen-root">
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-4">
          <h4 className="mb-1 fw-bold">Agent Workspace Filter Screen</h4>
          <p className="text-muted mb-4">
            Filter the call group pool, then start the next assignment
          </p>

          {filtersLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <Form onSubmit={handleGo}>
              {FILTER_ROWS.map((rowIds) => (
                <Row key={rowIds.join("-")}>
                  {rowIds.map((filterId) => (
                    <Col md={4} key={filterId}>
                      {renderFilterSelect(filterId)}
                    </Col>
                  ))}
                </Row>
              ))}

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || filtersLoading}
                  data-testid="btn-start-your-work"
                >
                  {submitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Starting…
                    </>
                  ) : (
                    "Start Your Work"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <AssignedActivitiesTable enabled={flowEnabled} />
      <FilteredCallGroupsCompleteModal
        show={showCompleteModal}
        onOkay={() => setShowCompleteModal(false)}
      />
    </Container>
  );
};

export default CallGroupFilterScreen;
